import React from 'react';

import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { type PartnerPermissions } from 'pages/partners';
import { type UserPermissions } from 'pages/users/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Container, Row } from 'react-bootstrap';
import { useNotification } from 'app/notifications';
import { User, UsersApi } from 'apis/UsersApi';
import { useSuperState } from 'hooks';

import 'pages/users/components/Details/styles/details.scss';

interface PreferencesProps {

    getAllUsers: (partnerId: number) => void;
    handleUserUpdate: (userDetails: User) => void;
    partnerId?: number;
    permissions: UserPermissions | PartnerPermissions;
    setUserDetails: React.Dispatch<React.SetStateAction<User>>;
    setUserDetailsOriginal: React.Dispatch<React.SetStateAction<User>>;
    userDetails: User;
    userDetailsOriginal: User;
}

export function Preferences(props: PreferencesProps) {

    const { userDetails } = props;

    const [state, setState] = useSuperState({
        loading: !userDetails,
        showResetPreferencesModal: false,
        showResetToursModal: false
    });

    const { loading, showResetPreferencesModal, showResetToursModal } = state;

    const { formatMessage } = useIntl();
    const { createToast } = useNotification();

    const handleResetPreferences = async () => {

        try {

            setState({ loading: true });
            await UsersApi.ResetPreferences();

            createToast(formatMessage({ id: 'preferences_reset_successfully.7b6ba112', defaultMessage: 'Preferences reset successfully' }), 'success');

        } catch (error) {

            createToast(formatMessage({ id: 'error_resetting_preferences.4ec8cc82', defaultMessage: 'Error resetting preferences' }), 'danger');

        } finally {

            setState({ loading: false });

        }

    };
    const handleResetAllTours = async () => {

        try {

            setState({ loading: true });
            await UsersApi.ResetAllTours();

            createToast(formatMessage({ id: 'preferences_reset_successfully.7b6ba112', defaultMessage: 'Preferences reset successfully' }), 'success');

        } catch (error) {

            createToast(formatMessage({ id: 'error_resetting_preferences.4ec8cc82', defaultMessage: 'Error resetting preferences' }), 'danger');

        } finally {

            setState({ loading: false });

        }

    };

    return (
        <div>
            <h3 className="text-strong mb-3">
                <FormattedMessage defaultMessage="Preferences" id="preferences.2bb53b2b" />
            </h3>
            {loading ? (
                <GMetrixLoader />
            ) : userDetails ? (
                <div className="p-4">
                    <div className="mb-3">
                        <div>
                            <div className="text-strong fw-semibold mb-2">
                                <FormattedMessage defaultMessage="Reset Site Preferences" id="reset_site_preferences.2c32a2c7" />
                            </div>
                            <p className="text-muted mb-3">
                                <FormattedMessage defaultMessage="Resetting site preferences will restore all view and filter settings on tables to their default state." id="resetting_site_preferences_will_restore.ef3eae0e" />
                            </p>
                            <GMetrixButton onClick={() => setState({ showResetPreferencesModal: true })} variant="outline-primary">
                                <FormattedMessage defaultMessage="Reset Preferences" id="reset_preferences.3312b82d" />
                            </GMetrixButton>
                        </div>
                    </div>
                    <div>
                        <div>
                            <div className="text-strong fw-semibold mb-2">
                                <FormattedMessage defaultMessage="Reset All On Screen Tours" id="reset_all_on_screen_tours.30deb8e9" />
                            </div>
                            <p className="text-muted mb-3">
                                <FormattedMessage defaultMessage="Restarting the on-screen tour replays the tutorial that introduces the user interface and key website features." id="restarting_the_on_screen_tour_replays_th.6c46b5f5" />
                            </p>
                            <GMetrixButton onClick={() => setState({ showResetToursModal: true })} variant="outline-primary">
                                <FormattedMessage defaultMessage="Reset Tours" id="reset_tours.1ea7cc6c" />
                            </GMetrixButton>
                        </div>
                    </div>

                </div>
            ) : (
                <div>
                    <FormattedMessage defaultMessage="Something went wrong while loading the user's details." id="something_went_wrong_while_loading_the_u.53d1cb7a" />
                </div>
            )}
            {/* Reset Site Preferences */}
            <GMetrixDialog
                showCancelButton
                actionButtonClick={async () => {

                    await handleResetPreferences();
                    setState({ showResetPreferencesModal: false });

                }}
                actionButtonDisabled={false}
                actionButtonText={formatMessage({ id: 'reset.43ed980c', defaultMessage: 'Reset' })}
                closeButtonText={formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' })}
                dialogLabel={formatMessage({ id: 'reset_site_preferences.2c32a2c7', defaultMessage: 'Reset Site Preferences' })}
                onCancel={() => setState({ showResetPreferencesModal: false })}
                show={showResetPreferencesModal}
            >

                <Container>
                    <Row>
                        <Col>
                            <p className="text-muted">
                                <FormattedMessage defaultMessage="Are you sure you want to reset site preferences?" id="are_you_sure_you_want_to_reset_site_pref.caf40b80" />
                            </p>
                        </Col>
                    </Row>
                </Container>
            </GMetrixDialog>

            {/* Reset All On Screen Tours */}
            <GMetrixDialog
                showCancelButton
                actionButtonClick={async () => {

                    await handleResetAllTours();
                    setState({ showResetToursModal: false });

                }}
                actionButtonDisabled={false}
                actionButtonText={formatMessage({ id: 'reset.43ed980c', defaultMessage: 'Reset' })}
                closeButtonText={formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' })}
                dialogLabel={formatMessage({ id: 'reset_all_on_screen_tours.30deb8e9', defaultMessage: 'Reset All On Screen Tours' })}
                onCancel={() => setState({ showResetToursModal: false })}
                show={showResetToursModal}
            >

                <Container>
                    <Row>
                        <Col>
                            <p className="text-muted">
                                <FormattedMessage defaultMessage="Are you sure you want to reset all on-screen tours?" id="are_you_sure_you_want_to_reset_all_on_sc.223f3f9b" />
                            </p>
                        </Col>
                    </Row>
                </Container>
            </GMetrixDialog>
        </div>
    );

}
